import React from 'react';
import { TFunction } from 'i18next';
import {
  LinkIcons,
  ListCheckIcons,
  MapPinIcons,
  MegaPhoneIcons,
  MicIcons,
  NotebookIcons,
  VideoIcons,
} from '../../../../../../../icons/icons';
import { ADMIN_PAGES_KEYS } from '../../../../../../../i18n/constants/keys';
import { ContextMenuButton, NameEmailAvatar } from '../../../../../../../ui';
import { ICandidate } from '../../../../../../../utils/types/JobOpenings';
import StatusLabel from '../Components/StatusLabel';
import StatusTracker from '../Components/StatusTracker';

interface GetColumnsArgs {
  handleOpenMenu: (
    e: React.MouseEvent<HTMLButtonElement>,
    row: ICandidate
  ) => void;
  handleCloseMenu: () => void;
  onRecordInterview?: (row: ICandidate) => void;
  onOpenPanelistNotes?: (row: ICandidate) => void;
  recordingRowId?: string | number | null;
  anchorEl: HTMLElement | null;
  t: TFunction;
}

export const getColumns = ({
  handleOpenMenu,
  handleCloseMenu,
  anchorEl,
  onRecordInterview,
  onOpenPanelistNotes,
  recordingRowId,
  t,
}: GetColumnsArgs) => {
  const SI = ADMIN_PAGES_KEYS.jobOpenings.smartInterviews;
  return [
  {
    key: 'applicant',
    label: t(SI.candidates.columns.applicant),
    width: '150px',
    align: 'left' as const,
    render: (row: ICandidate) => (
      <NameEmailAvatar
          email={row.email}
          name={`${row.firstName} ${row.lastName}`}
          avatar={''}
      />
    ),
  },
  {
    key: 'interviewType',
    label: t(SI.candidates.columns.interviewType),
    width: '130px',
    align: 'left' as const,
    render: (row: ICandidate) => (
      <div className='inline-flex items-center text-sm font-medium bg-gray-100 rounded-full border border-gray-200'>
        <div className='px-3 py-1 bg-white text-gray-900'>
          { row.interviewType }
        </div>
      </div>
    ),
    disableSort: true,
  },

  // ——— Link / Address Column ————————————————————————————————————
  {
    key: 'linkOrAddress',
    label: t(SI.candidates.columns.linkOrAddress),
    width: '220px',
    align: 'left' as const,
    render: (row: ICandidate) => {
      const isVirtual = row.interviewType.trim().toLowerCase() === 'virtual';
      const raw = isVirtual ? row.meetingLink || '' : '';
      // normalize external URL
      const href =
        raw.match(/^https?:\/\//i) !== null ? raw : raw ? `https://${raw}` : '';

      const text = isVirtual
        ? row.meetingLink || 'No Link'
        : row.inPersonAddress || 'No Address';
      const label = isVirtual ? 'Meeting:' : 'Address:';
      const Icon = isVirtual ? LinkIcons : MapPinIcons;
      const textColor = isVirtual ? 'text-indigo-600' : 'text-green-700';

      const content =
        isVirtual && href ? (
          <a
              href={href}
              target='_blank'
              rel='noopener noreferrer'
              className='ml-1 inline-block max-w-[150px] truncate underline'
              title={text}
          >
            { text }
          </a>
        ) : (
          <span
              className='ml-1 inline-block max-w-[150px] truncate'
              title={text}
          >
            { text || 'No Link' }
          </span>
        );

      return (
        <div className={`inline-flex items-center text-sm ${textColor}`}>
          <Icon className='mr-1' />
          <span className='flex items-center'>
            { label }
            { content }
          </span>
        </div>
      );
    },
    disableSort: true,
  },

  // ——————————————————————————————————————————————————————————————————————

  {
    key: 'interviewStartTime',
    label: t(SI.candidates.columns.dateTime),
    width: '120px',
    align: 'left' as const,
    render: (row: ICandidate) => {
      if (!row.interviewStartTime) {
        return <span className='text-gray-500'>No Date Scheduled</span>;
      }
      const date = new Date(Number(row.interviewStartTime) * 1000);
      const formatted = date.toLocaleString('en-US', {
        month: 'short',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
      return <span>{ formatted }</span>;
    },
    disableSort: true,
  },
  {
    key: 'interviewApplicantStatus',
    label: t(SI.candidates.columns.applicantStatus),
    width: '100px',
    align: 'left' as const,
    render: (row: ICandidate) => (
      <StatusTracker status={row.interviewApplicantStatus} />
    ),
    disableSort: true,
  },
  {
    key: 'PenalistNotes',
    label: t(SI.candidates.columns.notes),
    width: '80px',
    align: 'left' as const,
    render: (row: ICandidate) => (
      <button
          onClick={
            onOpenPanelistNotes ? () => onOpenPanelistNotes(row) : undefined
          }
          className={`p-2 rounded-full focus:outline-none ${
            row.hasPanelistNotes
              ? 'bg-emerald-100 text-emerald-600 hover:bg-emerald-200 hover:text-emerald-800'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800'
          }`}
          title={
            row.hasPanelistNotes
              ? 'View Panelist notes'
              : 'There is no Panelist notes yet'
          }
          disabled={!onOpenPanelistNotes}
      >
        <NotebookIcons />
      </button>
    ),
    disableSort: true,
  },
  {
    key: 'record',
    label: '',
    width: '40px',
    align: 'left' as const,
    render: (row: ICandidate) => {
      const hasAddress = !!row.inPersonAddress?.trim();
      const isRecording = row.id === recordingRowId;

      if (!hasAddress) {
        return <></>; // Don't render anything if there's no address
      }

      return (
        <button
            onClick={onRecordInterview ? () => onRecordInterview(row) : undefined}
            className={`p-2 rounded-full focus:outline-none ${
              isRecording
                ? 'bg-red-100 text-red-600 hover:bg-red-200 hover:text-red-800'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800'
            }`}
            title={isRecording ? 'Stop Recording' : 'Start Recording'}
            disabled={!onRecordInterview}
        >
          <MicIcons />
        </button>
      );
    },
    disableSort: true,
  },

  {
    key: 'actions',
    label: '',
    width: '40px',
    align: 'right' as const,
    render: (row: ICandidate) => (
      <ContextMenuButton
          row={row}
          onOpenMenu={(e, row) => handleOpenMenu(e, row as ICandidate)}
          onCloseMenu={handleCloseMenu}
          anchorEl={anchorEl}
      />
    ),
    disableSort: true,
  },
  ];
};
